/**
 * C-CDA documents — receiving an outside organization's clinical record.
 *
 * Replaces `src/api/cdaApi.js`: `#ccda`, `#requestAutoAddCda`,
 * `#requestCommitAutoAddCda`, `#cdaMedicationImport`, `#downloadCda` and
 * `#deleteCda`.
 *
 * **The pipeline has real intermediate states** — uploaded, scanning, staged,
 * committed — derived from the document's timestamps rather than stored, because
 * a status column and the timestamps that imply it are two statements of one
 * fact. Everything a scan proposes is written into the real clinical tables as a
 * draft with an editor, and every chart read filters drafts out. That is what
 * stops a half-imported document reading as part of the chart, and it is the
 * single most important property in this module: nothing an operator has not
 * committed is clinically visible.
 *
 * `commitCdaDocument` is therefore the one call that puts an outside
 * organization's content into the chart — one transaction across five clinical
 * tables. The legacy scan ran for up to 300 seconds synchronously inside the
 * upload request, and its commit branch was a no-op the SPA still asserted on.
 *
 * These carry `audit:cda_document` rather than `audit:document`: a C-CDA read is
 * a read of an entire outside record, and an auditor asking who opened one must
 * not have to filter it out of every file read.
 */

import api from './client';

export function listCdaDocuments(params = {}) {
  return api.list('/cda-documents', params);
}

/** Multipart: `patient_id` plus one part called `file`. */
export function uploadCdaDocument(formData, onProgress) {
  return api.upload('/cda-documents', formData, { onProgress });
}

export function getCdaDocument(cdaId) {
  return api.get(`/cda-documents/${cdaId}`);
}

export function downloadCdaDocument(cdaId) {
  return api.download(`/cda-documents/${cdaId}/content`);
}

export function deleteCdaDocument(cdaId) {
  return api.delete(`/cda-documents/${cdaId}`);
}

// --- staging ---------------------------------------------------------------

/** Idempotent: re-scanning supersedes the staged set rather than adding to it. */
export function scanCdaDocument(cdaId) {
  return api.post(`/cda-documents/${cdaId}/scan`);
}

/**
 * What the scan proposes, by kind: `measurements`, `diagnoses`, `medications`,
 * `vaccinations`, `procedures`.
 */
export function listStagedItems(cdaId) {
  return api.get(`/cda-documents/${cdaId}/staged-items`);
}

/**
 * Correct staged rows before commit. Which columns may change is decided by the
 * server, not here: an operator fixing a staged row is correcting what the
 * machine got wrong about a document somebody else wrote, not authoring clinical
 * content.
 */
export function updateStagedItems(cdaId, changes) {
  return api.patch(`/cda-documents/${cdaId}/staged-items`, changes);
}

/**
 * Leave one staged item out of the chart. `kind` is the staged set's own word
 * for it (`diagnoses`, `medications`, `vaccinations`, `procedures`,
 * `measurements`) because ids are per table: a staged medication 42 and a staged
 * diagnosis 42 are two items. The answer is the set that remains, with its
 * review notes read again.
 */
export function dropStagedItem(cdaId, kind, itemId) {
  return api.delete(`/cda-documents/${cdaId}/staged-items/${kind}/${itemId}`);
}

/**
 * Abandon the staged set without deleting the document. The document goes back
 * to `uploaded` when nothing of it was saved, and reads as saved when part of it
 * was.
 */
export function discardStagedItems(cdaId) {
  return api.delete(`/cda-documents/${cdaId}/staged-items`);
}

/**
 * Commit the staged items. With no selection, everything staged goes in - which
 * is what Save does. `itemIds` is the legacy flat list (an id selects that id in
 * every table); `items` is `[{kind, id}]`, which names one row of one table.
 */
export function commitCdaDocument(cdaId, itemIds, items) {
  const body = {};

  if (itemIds !== undefined) body.item_ids = itemIds;
  if (items !== undefined) body.items = items;

  return api.post(`/cda-documents/${cdaId}/commit`, body);
}

/**
 * The medication fields a staged drug carries, for the import form. Two scopes,
 * ANDed — the answer is medication content read out of a document, so holding
 * either alone is not enough.
 *
 * `items` is one per row of the side bar: `{key, html_name, date, end_date}`.
 * The answer's `fields` are keyed by `key`, so two courses of one drug (one
 * anchor, two rows) are answered apart; `date` and `end_date` choose the course.
 */
export function getMedicationImportFields(cdaId, items) {
  return api.post(`/cda-documents/${cdaId}/medication-import-fields`, { items });
}

export default {
  listCdaDocuments, uploadCdaDocument, getCdaDocument, downloadCdaDocument,
  deleteCdaDocument,
  scanCdaDocument, listStagedItems, updateStagedItems, dropStagedItem, discardStagedItems,
  commitCdaDocument, getMedicationImportFields,
};
